// @version es6
import '@archetype-themes/scripts/config'
import '@archetype-themes/scripts/helpers/currency'
import '@archetype-themes/scripts/helpers/images'
import '@archetype-themes/scripts/helpers/variants'
import '@archetype-themes/scripts/modules/variant-availability'
import '@archetype-themes/scripts/modules/quantity-selectors'
import '@archetype-themes/scripts/modules/product-ajax-form'
import '@archetype-themes/scripts/helpers/youtube'
import '@archetype-themes/scripts/helpers/vimeo'
import { Slideshow } from '@archetype-themes/scripts/modules/slideshow'
import '@archetype-themes/scripts/modules/product-media'
import { StoreAvailability } from 'components/section-store-availability'
import { RecentlyViewed } from 'components/section-recently-viewed'

export default class Product extends HTMLElement {
  constructor (container) {
    super();
    this.container = container ?? this;
    this.boundReturnFocusToThumbnail = this.returnFocusToThumbnail.bind(this)
    this.isInitialized = false
    this.deferredVideoHydrationScheduled = false
    if (!!this.container.dataset.modal) {
      this.init();
    }
  }

  connectedCallback() {
    if (!this.isInitialized && !this.container.dataset.modal) {
      this.init()
    }
  }

  init() {
    if (this.isInitialized) return
    this.isInitialized = true
    this.videoObjects = {}

    this.classes = {
      onSale: 'on-sale',
      disabled: 'disabled',
      loading: 'loading',
      loaded: 'loaded',
      hidden: 'hide',
      interactable: 'video-interactable',
      visuallyHide: 'visually-invisible',
      lowInventory: 'inventory--low',
    }

    this.selectors = {
      productVideo: '.product__video',
      videoParent: '.product__video-wrapper',
      slide: '.product-main-slide',
      currentSlide: '.is-selected',
      startingSlide: '.starting-slide',
      variantType: '.variant-wrapper',
      blocks: '[data-product-blocks]',
      blocksHolder: '[data-blocks-holder]',
      dynamicVariantsEnabled: '[data-dynamic-variants-enabled]',
      variantsJson: '[data-variant-json]',
      currentVariantJson: '[data-current-variant-json]',
      form: '.product-single__form',
      media: '[data-product-media-type-model]',
      closeMedia: '.product-single__close-media',
      photoThumbs: '[data-product-thumb]',
      thumbSlider: '[data-product-thumbs]',
      thumbScroller: '.product__thumbs--scroller',
      mainSlider: '[data-product-photos]',
      imageContainer: '[data-product-images]',
      productImageMain: '[data-product-image-main]',
      priceWrapper: '[data-product-price-wrap]',
      price: '[data-product-price]',
      comparePrice: '[data-compare-price]',
      savePrice: '[data-save-price]',
      priceA11y: '[data-a11y-price]',
      comparePriceA11y: '[data-compare-price-a11y]',
      unitWrapper: '[data-unit-price-wrapper]',
      unitPrice: '[data-unit-price]',
      unitPriceBaseUnit: '[data-unit-base]',
      sku: '[data-sku]',
      inventory: '[data-product-inventory]',
      incomingInventory: '[data-incoming-inventory]',
      colorLabel: '[data-variant-color-label]',
      variantLabel: '.variant-input .variant__button-label',
      addToCart: '[data-add-to-cart]',
      addToCartText: '[data-add-to-cart-text]',
      originalSelectorId: '[data-product-select]',
      singleOptionSelector: '[data-variant-input]',
      variantColorSwatch: '.variant__input--color-swatch',
      availabilityContainer: '[data-store-availability-holder]',
    }

    this.sectionId = this.container.getAttribute('data-section-id')
    this.productId = this.container.getAttribute('data-product-id')

    document.dispatchEvent(new CustomEvent('product-component:loaded', {
      detail: { sectionId: this.sectionId }
    }))

    document.addEventListener('shopify:section:unload', this.onUnload.bind(this))

    this.settings = {
      enableHistoryState: (this.container.dataset.history === 'true') || false,
      namespace: `.product-${this.sectionId}`,
      inventory: false,
      inventoryThreshold: 10,
      modalInit: false,
      hasImages: true,
      imageSetName: null,
      imageSetIndex: null,
      currentImageSet: null,
      imageSize: '620x',
      currentSlideIndex: 0,
      videoLooping: this.container.dataset.videoLooping,
      mediaGalleryLayout: this.container.querySelector('[data-product-images]')?.dataset.mediaGalleryLayout,
      hasVideos: this.container.querySelector(this.selectors.productVideo) ? true : false,
      hasModels: this.container.querySelector('[data-product-media-type-model]') ? true : false,
      priceRuleVariants: JSON.parse(this.container.dataset.priceRuleVariants || '{}'),
    }

    this.cacheElements()
    this.firstProductImage = this.cache.mainSlider.querySelector('img')
    if (!this.firstProductImage) {
      this.settings.hasImages = false
    }

    const dataSetEl = this.cache.mainSlider.querySelector('[data-set-name]')
    if (dataSetEl) {
      this.settings.imageSetName = dataSetEl.dataset.setName
    }

    this.formSetup()
    this.updateModalProductInventory()
    this.productSetup()
    this.videoSetup()
    this.initProductSlider()
    this.customMediaListeners()
    RecentlyViewed.addIdToRecentlyViewed(this.productId)

    document.addEventListener('tooltip:close', e => {
      if (e.detail.context === 'QuickShop') {
        this.stopVideos();
      }
    })

    window.off('quickadd:loaded:' + this.sectionId)
  }

  cacheElements() {
    this.cache = {
      form: this.container.querySelector(this.selectors.form),
      mainSlider: this.container.querySelector(this.selectors.mainSlider),
      thumbSlider: this.container.querySelector(this.selectors.thumbSlider),
      thumbScroller: this.container.querySelector(this.selectors.thumbScroller),
      productImageMain: this.container.querySelector(this.selectors.productImageMain),
      priceWrapper: this.container.querySelector(this.selectors.priceWrapper),
      comparePriceA11y: this.container.querySelector(this.selectors.comparePriceA11y),
      comparePrice: this.container.querySelector(this.selectors.comparePrice),
      price: this.container.querySelector(this.selectors.price),
      savePrice: this.container.querySelector(this.selectors.savePrice),
      priceA11y: this.container.querySelector(this.selectors.priceA11y)
    }
  }

  formSetup() {
    this.initQtySelector()
    this.initAjaxProductForm()
    this.availabilitySetup()
    this.initVariants()
    if (this.settings.imageSetName) {
      this.updateImageSet()
    }
  }

  availabilitySetup() {
    const container = this.container.querySelector(this.selectors.availabilityContainer)
    if (container) {
      this.storeAvailability = new StoreAvailability(container)
    }
  }

  productSetup() {
    this.initImageZoom()
    this.initModelViewerLibraries()
    this.initShopifyXrLaunch()
    this.bindThumbnailEvents()
  }

  initVariants() {
    const variantJson = this.container.querySelector(this.selectors.variantsJson)
    if (!variantJson) return

    this.variantsObject = JSON.parse(variantJson.innerHTML)
    const dynamicVariantsEnabled = !!this.container.querySelector(this.selectors.dynamicVariantsEnabled)

    const options = {
      container: this.container,
      enableHistoryState: this.settings.enableHistoryState,
      singleOptionSelector: this.selectors.singleOptionSelector,
      originalSelectorId: this.selectors.originalSelectorId,
      variants: this.variantsObject,
      dynamicVariantsEnabled
    }

    const swatches = this.container.querySelectorAll(this.selectors.variantColorSwatch)
    if (swatches.length) {
      swatches.forEach(swatch => {
        swatch.addEventListener('change', function (evt) {
          const color = swatch.dataset.colorName
          const index = swatch.dataset.colorIndex
          this.updateColorName(color, index)
        }.bind(this))
      })
    }

    const labels = this.container.querySelectorAll(this.selectors.variantLabel)
    if (labels.length) {
      labels.forEach(label => {
        label.addEventListener('keydown', function (evt) {
          if (evt.keyCode === 13) {
            const input = document.querySelector(`#${label.getAttribute('for')}`)
            if (!input) return
            input.checked = true
            input.dispatchEvent(new Event('change'))
          }
        }.bind(this))
      })
    }

    this.variants = new theme.Variants(options)
    this.refreshQuantityOptions(this.variants.currentVariant)

    if (this.storeAvailability) {
      const variant_id = this.variants.currentVariant ? this.variants.currentVariant.id : this.variants.variants[0].id
      this.storeAvailability.updateContent(variant_id)
      this.container.on('variantChange' + this.settings.namespace, this.updateAvailability.bind(this))
    }

    this.container.on('variantChange' + this.settings.namespace, this.refreshQuantityOptions.bind(this))
    this.container.on('variantChange' + this.settings.namespace, this.updateCartButton.bind(this))
    this.container.on('variantImageChange' + this.settings.namespace, this.updateVariantImage.bind(this))
    this.container.addEventListener('variantPriceChange', this.updatePrice.bind(this))
    this.container.on('variantUnitPriceChange' + this.settings.namespace, this.updateUnitPrice.bind(this))

    if (this.container.querySelectorAll(this.selectors.sku).length) {
      this.container.on('variantSKUChange' + this.settings.namespace, this.updateSku.bind(this))
    }

    const inventoryEl = this.container.querySelector(this.selectors.inventory)
    if (inventoryEl) {
      this.settings.inventory = true
      this.settings.inventoryThreshold = inventoryEl.dataset.threshold
      this.updateInventory({ detail: { variant: this.variants.currentVariant } })
      this.container.on('variantChange' + this.settings.namespace, this.updateInventory.bind(this))
    }

    if (dynamicVariantsEnabled) {
      const currentVariantJson = this.container.querySelector(this.selectors.currentVariantJson)
      if (currentVariantJson) {
        const variantType = this.container.querySelector(this.selectors.variantType)
        if (variantType) {
          new theme.VariantAvailability({
            container: this.container,
            namespace: this.settings.namespace,
            type: variantType.dataset.type,
            variantsObject: this.variantsObject,
            currentVariantObject: JSON.parse(currentVariantJson.innerHTML)
          })
        }
      }
    }

    if (this.settings.imageSetName) {
      const variantWrapper = this.container.querySelector('.variant-input-wrap[data-handle="' + this.settings.imageSetName + '"]')
      if (variantWrapper) {
        this.settings.imageSetIndex = variantWrapper.dataset.index
        this.container.on('variantChange' + this.settings.namespace, this.updateImageSet.bind(this))
      } else {
        this.settings.imageSetName = null
      }
    }
  }

  initQtySelector() {
    if (typeof theme === 'undefined' || typeof theme.QtySelector === 'undefined') return
    this.container.querySelectorAll('.js-qty__wrapper').forEach(el => {
      if (!el.querySelector('.js-qty__adjust')) return
      new theme.QtySelector(el, { namespace: '.product' })
    })
  }

  initAjaxProductForm() {
    new theme.AjaxProduct(this.cache.form, '.add-to-cart');
  }

  refreshQuantityOptions(variantOrEvent, inventoryData = null) {
    const select = this.container.querySelector('.product__quantity-select');
    if (!select) return null;

    const fallbackMax = 48;
    let variant = variantOrEvent;
    if (variantOrEvent && variantOrEvent.detail) {
      variant = variantOrEvent.detail.variant;
    }

    let maxQuantity = fallbackMax;

    if (inventoryData && Object.prototype.hasOwnProperty.call(inventoryData, 'quantity')) {
      const inventoryQuantity = parseInt(inventoryData.quantity, 10);
      if (inventoryData.policy === 'continue') {
        maxQuantity = fallbackMax;
      } else if (!Number.isNaN(inventoryQuantity)) {
        maxQuantity = inventoryQuantity;
      } else {
        maxQuantity = 1;
      }
    } else if (variant) {
      if (variant.inventory_management === 'shopify') {
        if (variant.inventory_policy === 'continue') {
          maxQuantity = fallbackMax;
        } else {
          const variantQuantity = parseInt(variant.inventory_quantity, 10);
          maxQuantity = Number.isNaN(variantQuantity) ? 1 : variantQuantity;
        }
      } else {
        maxQuantity = fallbackMax;
      }
    }

    if (!variant) maxQuantity = 1;
    if ((inventoryData && inventoryData.policy === 'continue') || (variant && (!variant.inventory_management || variant.inventory_policy === 'continue'))) {
      maxQuantity = Math.max(1, fallbackMax);
    }
    if (!maxQuantity || maxQuantity < 1) maxQuantity = 1;

    const previousValue = parseInt(select.value, 10) || 1;
    const previousMax = parseInt(select.dataset.maxQuantity || '0', 10) || 0;

    if (previousMax !== maxQuantity || select.options.length !== maxQuantity) {
      const fragment = document.createDocumentFragment();
      for (let i = 1; i <= maxQuantity; i += 1) {
        const option = document.createElement('option');
        option.value = `${i}`;
        option.textContent = `${i}`;
        fragment.appendChild(option);
      }
      select.innerHTML = '';
      select.appendChild(fragment);
    }

    select.dataset.maxQuantity = `${maxQuantity}`;
    let nextValue = Math.min(Math.max(previousValue, 1), maxQuantity);
    if (!select.querySelector(`option[value="${nextValue}"]`)) {
      nextValue = select.options.length ? parseInt(select.options[select.options.length - 1].value, 10) || 1 : 1;
    }

    const valueChanged = select.value !== `${nextValue}`;
    if (valueChanged) {
      select.value = `${nextValue}`;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return maxQuantity;
  }

  updateColorName(color, index) {
    this.container.querySelector(this.selectors.colorLabel + `[data-index="${index}"`).textContent = color
  }

  updateCartButton(evt) {
    const variant = evt.detail.variant
    const cartBtn = this.container.querySelector(this.selectors.addToCart)
    const cartBtnText = this.container.querySelector(this.selectors.addToCartText)
    if (!cartBtn) return

    if (variant) {
      if (variant.available) {
        cartBtn.classList.remove(this.classes.disabled)
        cartBtn.disabled = false
        cartBtnText.textContent = cartBtnText.dataset.defaultText
      } else {
        cartBtn.classList.add(this.classes.disabled)
        cartBtn.disabled = true
        cartBtnText.textContent = theme.strings.soldOut
      }
    } else {
      cartBtn.classList.add(this.classes.disabled)
      cartBtn.disabled = true
      cartBtnText.textContent = theme.strings.unavailable
    }
  }

  checkPriceRule(variant) {
    return this.settings.priceRuleVariants[variant.id] ?? true
  }

  updatePrice(evt) {
    const variant = evt.detail.variant
    if (!this.cache.price) this.cacheElements()

    if (!variant || !variant.available) {
      this.cache.price?.classList.add(this.classes.hidden)
      this.cache.priceWrapper?.classList.add(this.classes.hidden)
      this.cache.comparePrice?.classList.add(this.classes.hidden)
      this.cache.savePrice?.classList.add(this.classes.hidden)
      return
    }

    const hideSale = this.checkPriceRule(variant)
    const priceBlock = this.cache.price?.closest('.product-block--price') || this.container.querySelector('.product-block--price')
    if (priceBlock) priceBlock.setAttribute('data-hide-sale', hideSale ? 'true' : 'false')

    this.cache.price.classList.remove(this.classes.hidden)
    this.cache.priceWrapper?.classList.remove(this.classes.hidden)
    this.cache.comparePrice?.classList.remove(this.classes.hidden)
    this.cache.savePrice?.classList.remove(this.classes.hidden)
    const formattedPrice = this.formatPdpMoney(variant.price)
    this.cache.price.innerHTML = `<span aria-hidden="true">${formattedPrice}</span>`

    if (variant.compare_at_price > variant.price && !hideSale) {
      const formattedComparePrice = this.formatPdpMoney(variant.compare_at_price)
      if (this.cache.comparePrice) this.cache.comparePrice.innerHTML = `<span aria-hidden="true">${formattedComparePrice}</span>`
      this.cache.priceWrapper?.classList.remove(this.classes.hidden)
      this.cache.price.classList.add(this.classes.onSale)
      const saleLabel = this.cache.comparePriceA11y?.dataset.salePriceLabel || 'Sale price'
      const regularLabel = this.cache.comparePriceA11y?.dataset.regularPriceLabel || 'Regular price'
      const priceLabel = `${saleLabel} ${formattedPrice} ${regularLabel} ${formattedComparePrice}`.trim()
      if (this.cache.comparePriceA11y) this.cache.comparePriceA11y.textContent = ''
      this.cache.comparePriceA11y?.setAttribute('aria-hidden', 'true')
      priceBlock?.setAttribute('aria-label', priceLabel)
      this.cache.priceA11y?.setAttribute('aria-hidden', 'true')

      let savings = variant.compare_at_price - variant.price
      if (theme.settings.saveType === 'percent') {
        savings = Math.round(((savings) * 100) / variant.compare_at_price) + '%'
      } else {
        savings = this.formatPdpMoney(savings)
      }
      this.cache.savePrice?.classList.remove(this.classes.hidden)
      if (this.cache.savePrice) this.cache.savePrice.innerHTML = theme.strings.savePrice.replace('[saved_amount]', savings)
    } else {
      this.cache.priceWrapper?.classList.add(this.classes.hidden)
      this.cache.savePrice?.classList.add(this.classes.hidden)
      this.cache.price.classList.remove(this.classes.onSale)
      const regularLabel = this.cache.comparePriceA11y?.dataset.regularPriceLabel || 'Regular price'
      const priceLabel = `${regularLabel} ${formattedPrice}`.trim()
      if (this.cache.comparePriceA11y) this.cache.comparePriceA11y.textContent = ''
      priceBlock?.setAttribute('aria-label', priceLabel)
      this.cache.comparePriceA11y?.setAttribute('aria-hidden', 'true')
      this.cache.priceA11y?.setAttribute('aria-hidden', 'true')
    }
  }

  formatPdpMoney(cents) {
    return theme.Currency
      .formatMoney(cents, theme.settings.moneyFormat)
      .replace(/<sup[^>]*>(.*?)<\/sup>/gi, '.$1')
      .replace(/<[^>]+>/g, '')
  }

  updateUnitPrice(evt) {
    const variant = evt.detail.variant
    if (variant && variant.unit_price) {
      this.container.querySelector(this.selectors.unitPrice).innerHTML = theme.Currency.formatMoney(variant.unit_price, theme.settings.moneyFormat)
      this.container.querySelector(this.selectors.unitPriceBaseUnit).innerHTML = theme.Currency.getBaseUnit(variant)
      this.container.querySelector(this.selectors.unitWrapper).classList.remove(this.classes.hidden)
    } else {
      this.container.querySelector(this.selectors.unitWrapper).classList.add(this.classes.hidden)
    }
  }

  imageSetArguments(variant) {
    variant = variant ? variant : (this.variants ? this.variants.currentVariant : null)
    if (!variant) return
    const setValue = this.settings.currentImageSet = this.getImageSetName(variant[this.settings.imageSetIndex])
    const set = `${this.settings.imageSetName}_${setValue}`
    this.settings.currentSlideIndex = 0
    return { cellSelector: '[data-group="' + set + '"]', imageSet: set, initialIndex: this.settings.currentSlideIndex }
  }

  updateImageSet(evt) {
    const variant = evt ? evt.detail.variant : (this.variants ? this.variants.currentVariant : null)
    if (!variant) return
    const setValue = this.getImageSetName(variant[this.settings.imageSetIndex])
    if (this.settings.currentImageSet === setValue) return
    this.initProductSlider(variant)
  }

  updateImageSetThumbs(set) {
    this.cache.thumbSlider.querySelectorAll('.product__thumb-item').forEach(thumb => {
      thumb.classList.toggle(this.classes.hidden, thumb.dataset.group !== set)
    })
  }

  getImageSetName(string) {
    return string.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '')
  }

  updateSku(evt) {
    const variant = evt.detail.variant
    let newSku = '', hideSku = true
    if (variant && variant.sku) {
      newSku = variant.sku
      hideSku = false
    }
    this.container.querySelectorAll(this.selectors.sku).forEach(el => {
      el.classList.toggle(this.classes.hidden, hideSku)
      el.querySelector('[data-sku-id]').textContent = newSku
    })
  }

  updateInventory(evt) {
    const variant = evt.detail.variant
    if (!variant) {
      this.refreshQuantityOptions(variant);
      this.toggleInventoryQuantity('hidden', false);
      this.toggleIncomingInventory(false);
      return;
    }
    if (!variant.inventory_management) {
      this.refreshQuantityOptions(variant);
      this.toggleInventoryQuantity('visible', false);
      this.toggleIncomingInventory(false);
      return
    }
    if (variant.inventory_management === 'shopify' && window.inventories && window.inventories[this.productId]) {
      const variantInventoryObject = window.inventories[this.productId][variant.id]
      const { quantity, policy, incoming, next_incoming_date } = variantInventoryObject || {}
      this.refreshQuantityOptions(variant, variantInventoryObject);
      this.toggleInventoryQuantity(undefined, quantity);
      if ((incoming && !variant.available) || (quantity <= 0 && policy === 'continue')) {
        this.toggleIncomingInventory(true, next_incoming_date, policy);
      } else {
        this.toggleIncomingInventory(false)
      }
    } else {
      this.refreshQuantityOptions(variant);
    }
  }

  updateAvailability(evt) {
    const variant = evt.detail.variant
    if (!variant) return
    this.storeAvailability.updateContent(variant.id)
  }

  toggleInventoryQuantity(state = undefined, quantity) {
    const productInventoryEl = this.container.querySelector(this.selectors.inventory)
    if (!productInventoryEl) return
    const inventorySalesPoint = productInventoryEl.closest('.sales-point')

    if (state === 'hidden') {
      inventorySalesPoint?.classList.add(this.classes.hidden);
      return;
    }

    const showLowInventory = parseInt(quantity) <= parseInt(this.settings.inventoryThreshold) && parseInt(quantity) > 0

    if (parseInt(quantity) > 0 || state === 'visible') {
      if (showLowInventory) {
        productInventoryEl.parentNode.classList.add(this.classes.lowInventory)
        productInventoryEl.textContent = quantity > 1 
          ? theme.strings.otherStockLabel.replace('[count]', quantity)
          : theme.strings.oneStockLabel.replace('[count]', quantity)
      } else {
        productInventoryEl.parentNode.classList.remove(this.classes.lowInventory)
        productInventoryEl.textContent = theme.strings.inStockLabel
      }
      inventorySalesPoint?.classList.remove(this.classes.hidden)
    } else {
      inventorySalesPoint?.classList.add(this.classes.hidden)
    }
  }

  toggleIncomingInventory(showIncomingInventory, incomingInventoryDate, policy) {
    const incomingInventoryEl = this.container.querySelector(this.selectors.incomingInventory)
    if (!incomingInventoryEl) return
    const incomingInventoryIcon = incomingInventoryEl.querySelector('.icon-and-text');
    const textEl = incomingInventoryEl.querySelector('.js-incoming-text')
    if (incomingInventoryEl.dataset.enabled !== 'true') {
      incomingInventoryEl.classList.add(this.classes.hidden)
      return
    }
    if (showIncomingInventory) {
      textEl.textContent = incomingInventoryDate 
        ? theme.strings.willBeInStockAfter.replace('[date]', incomingInventoryDate)
        : theme.strings.waitingForStock
      incomingInventoryEl.classList.remove(this.classes.hidden)
      if (incomingInventoryIcon) {
        incomingInventoryIcon.classList.toggle(this.classes.lowInventory, policy !== 'continue')
      }
    } else {
      incomingInventoryEl.classList.add(this.classes.hidden)
    }
  }

  videoSetup() {
    const productVideos = this.cache.mainSlider.querySelectorAll(this.selectors.productVideo)
    if (!productVideos.length) return false
    productVideos.forEach(vid => {
      const type = vid.dataset.videoType
      if (type === 'youtube') this.initYoutubeVideo(vid)
      else if (type === 'vimeo') this.initVimeoVideo(vid)
      else if (type === 'mp4') this.initMp4Video(vid)
    })
    this.scheduleDeferredVideoHydration(productVideos)
  }

  initYoutubeVideo(div) {
    this.videoObjects[div.id] = new theme.YouTube(div.id, {
      videoId: div.dataset.videoId,
      videoParent: this.selectors.videoParent,
      autoplay: false,
      style: div.dataset.videoStyle,
      loop: div.dataset.videoLoop,
      events: {
        onReady: this.youtubePlayerReady.bind(this),
        onStateChange: this.youtubePlayerStateChange.bind(this)
      }
    })
  }

  initVimeoVideo(div) {
    this.videoObjects[div.id] = new theme.VimeoPlayer(div.id, div.dataset.videoId, {
      videoParent: this.selectors.videoParent,
      autoplay: false,
      style: div.dataset.videoStyle,
      loop: div.dataset.videoLoop,
    })
  }

  youtubePlayerReady(evt) {
    const iframeId = evt.target.getIframe().id
    if (!this.videoObjects[iframeId]) return
    const obj = this.videoObjects[iframeId]
    const player = obj.videoPlayer
    if (obj.options.style !== 'sound') player.mute()
    obj.parent.classList.remove('loading')
    obj.parent.classList.add('loaded', 'video-interactable')
    if (this._isFirstSlide(iframeId) && obj.options.style !== 'sound') player.playVideo()
  }

  _isFirstSlide(id) {
    return this.cache.mainSlider.querySelector(this.selectors.startingSlide + ' #' + id)
  }

  youtubePlayerStateChange(evt) {
    const iframeId = evt.target.getIframe().id
    const obj = this.videoObjects[iframeId]
    switch (evt.data) {
      case -1: if (obj.attemptedToPlay) obj.parent.classList.add('video-interactable'); break
      case 0: if (obj?.options.loop === 'true') obj.videoPlayer.playVideo(); break
      case 3: obj.attemptedToPlay = true; break
    }
  }

  initMp4Video(div) {
    this.videoObjects[div.id] = { id: div.id, type: 'mp4' }
    const player = this.container.querySelector('#' + div.id);
    player.addEventListener('loadedmetadata', () => {
      this.flickity?.resize();
      if (this._isFirstSlide(div.id) || this.settings.currentSlideIndex === Number(div.closest('.product-main-slide').dataset.index)) {
        this.playMp4Video(div.id);
      }
    });
    player.addEventListener('canplay', () => this.flickity?.resize());
    if (this._isFirstSlide(div.id)) this.playMp4Video(div.id)
  }

  hydrateDeferredVideo(player) {
    if (!player || player.dataset.videoHydrated === 'true') return false
    const deferredSources = player.querySelectorAll('source[data-src]')
    if (!deferredSources.length) {
      player.dataset.videoHydrated = 'true'
      return false
    }
    deferredSources.forEach(source => {
      source.src = source.dataset.src
      source.removeAttribute('data-src')
    })
    player.dataset.videoHydrated = 'true'
    if (typeof player.load === 'function') player.load()
    return true
  }

  scheduleDeferredVideoHydration(productVideos) {
    if (this.deferredVideoHydrationScheduled) return
    const deferredVideos = Array.from(productVideos).filter(video => video.dataset.deferredVideo === 'true')
    if (!deferredVideos.length) return

    this.deferredVideoHydrationScheduled = true

    const hydrateVideos = () => {
      deferredVideos.forEach(video => this.hydrateDeferredVideo(video))
    }
    const hydrateWhenIdle = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(hydrateVideos, { timeout: 5000 })
      } else {
        window.setTimeout(hydrateVideos, 3000)
      }
    }
    const hydrateAfterLoad = () => {
      window.setTimeout(hydrateWhenIdle, 3000)
    }

    if (document.readyState === 'complete') {
      hydrateAfterLoad()
    } else {
      window.addEventListener('load', hydrateAfterLoad, { once: true })
    }
  }

  stopVideos() {
    for (const [id, vid] of Object.entries(this.videoObjects)) {
      if (vid.videoPlayer?.stopVideo) vid.videoPlayer.stopVideo()
      else if (vid.type === 'mp4') this.stopMp4Video(vid.id)
    }
  }

  _getVideoType(video) { return video.getAttribute('data-video-type') }
  _getVideoDivId(video) { return video.id }

  playMp4Video(id) {
    const player = this.container.querySelector('#' + id)
    if (!player) return
    this.hydrateDeferredVideo(player)
    const playPromise = player.play()
    player.setAttribute('controls', '')
    player.focus()
    player.addEventListener('focusout', this.boundReturnFocusToThumbnail)
    playPromise?.catch(() => {
      player.setAttribute('controls', '')
      player.closest(this.selectors.videoParent)?.setAttribute('data-video-style', 'unmuted')
    })
    player.addEventListener('play', () => this.flickity?.resize());
  }

  stopMp4Video(id) {
    const player = this.container.querySelector('#' + id);
    if (!player) return;
    player.removeEventListener('focusout', this.boundReturnFocusToThumbnail)
    if (typeof player.pause === 'function') {
      player.removeAttribute('controls')
      player.pause()
    }
  }

  returnFocusToThumbnail(evt) {
    if (evt.relatedTarget?.classList.contains('product__thumb')) {
      const thumb = this.container.querySelector('.product__thumb-item[data-index="' + this.settings.currentSlideIndex + '"] [data-product-thumb]')
      thumb?.focus()
    }
  }

  initImageZoom() {
    const container = this.container.querySelector(this.selectors.imageContainer);
    if (!container) return;

    // PhotoSwipe construction — guard against missing module (e.g. Googlebot resource failure)
    if (typeof theme !== 'undefined' && typeof theme.Photoswipe === 'function') {
      const inProductImages = !!container.closest('product-images');
      const productImagesDefined = typeof window.customElements !== 'undefined' && !!window.customElements.get('product-images');

      // Keep both checks: web-component init path and cross-path/race duplicate guard.
      // Only skip when the web component is actually defined (so connectedCallback can run).
      const managedByProductImagesComponent = inProductImages && productImagesDefined;
      if (
        container.dataset.zoom !== 'false' &&
        !managedByProductImagesComponent &&
        container.dataset.photoswipeBound !== 'true'
      ) {
        try {
          this.photoswipe = new theme.Photoswipe(container, this.sectionId);
        } catch (error) {
          console.error('PhotoSwipe initialization failed:', error);
        }
      }
    }

    container.addEventListener('photoswipe:afterChange', (evt) => {
      this.flickity?.goToSlide(evt.detail.index);
    });
    document.addEventListener('tooltip:open', (e) => {
      if (e.detail.context !== 'QuickShop') return;
      document.querySelectorAll('tool-tip product-component script[type="module"]').forEach(s => {
        const script = document.createElement('script');
        script.type = 'module';
        script.textContent = s.textContent;
        s.parentNode.replaceChild(script, s);
      });
    });
  }

  bindThumbnailEvents() {
    if (!this.cache.thumbSlider) return
    this.cache.thumbSlider.addEventListener('click', this.onThumbnailClick.bind(this))
    this.cache.thumbSlider.addEventListener('keydown', this.onThumbnailKeydown.bind(this))
  }

  onThumbnailClick(evt) {
    const trigger = evt.target.closest(this.selectors.photoThumbs)
    if (!trigger) return
    if (trigger.tagName.toLowerCase() === 'a') evt.preventDefault()
    evt.stopPropagation()
    this.showMediaFromThumbnail(trigger)
  }

  onThumbnailKeydown(evt) {
    if (!['', 'Spacebar', 'Space', 'Enter'].includes(evt.key)) return
    const trigger = evt.target.closest(this.selectors.photoThumbs)
    if (!trigger) return
    evt.preventDefault()
    this.showMediaFromThumbnail(trigger)
  }

  showMediaFromThumbnail(trigger) {
    const index = Number.parseInt(trigger.dataset.index, 10)
    if (Number.isNaN(index)) return

    if (!theme.config.bpSmall && this.settings.mediaGalleryLayout === 'stacked') {
      this.scrollToStackedMedia(index)
      this.handleStackedMediaChange(index)
    } else if (this.flickity) {
      this.flickity.goToSlide(index)
      const slide = this.cache.mainSlider.querySelector(`.product-main-slide[data-index="${index}"]`);
      if (slide) this.prepMediaOnSlide(slide);
    } else {
      const slides = this.cache.mainSlider?.querySelectorAll(this.selectors.slide) || []
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-selected', i === index)
        slide.setAttribute('tabindex', i === index ? 0 : -1)
      })
      if (slides[index]) this.prepMediaOnSlide(slides[index]);
    }
    this.settings.currentSlideIndex = index
  }

  getThumbIndex(target) { return target?.dataset?.index }

  updateVariantImage(evt) {
    const variant = evt.detail.variant;
    if (!variant?.featured_media?.id) return;

    if (!theme.config.bpSmall && this.settings.mediaGalleryLayout === 'stacked') {
      const slide = this.cache.mainSlider.querySelector(`.product-main-slide[data-media-id="${variant.featured_media.id}"]`);
      if (!slide) return;
      const imageIndex = this.getThumbIndex(slide);
      this.scrollToStackedMedia(imageIndex);
      this.handleStackedMediaChange(imageIndex);
    } else {
      const newImage = this.container.querySelector('.product__thumb[data-id="' + variant.featured_media.id + '"]');
      if (!newImage) return;
      const imageIndex = this.getThumbIndex(newImage);
      if (typeof imageIndex === 'undefined') return
      this.flickity?.goToSlide(imageIndex)
    }
  }

  handleStackedMediaChange(index) {
    const mediaTarget = this.container.querySelectorAll('.product-slideshow .product-main-slide')[index];
    if (!mediaTarget) return;

    if (this.settings.hasVideos) {
      this.stopVideos();
      const video = mediaTarget.querySelector(this.selectors.productVideo);
      if (video) {
        const videoType = this._getVideoType(video);
        const videoId = this._getVideoDivId(video);
        if (videoType === 'youtube' && this.videoObjects[videoId]?.videoPlayer && this.videoObjects[videoId].options.style !== 'sound') {
          setTimeout(() => this.videoObjects[videoId].videoPlayer.playVideo(), 1000);
          return;
        } else if (videoType === 'mp4') {
          this.playMp4Video(videoId);
        }
      }
    }

    if (this.settings.hasModels) {
      this.container.querySelectorAll(this.selectors.media).forEach(el => {
        el.dispatchEvent(new CustomEvent('mediaHidden', { bubbles: true, cancelable: true }));
      });
      const currentMedia = mediaTarget.querySelector(this.selectors.media);
      if (currentMedia) {
        currentMedia.dispatchEvent(new CustomEvent('mediaVisible', { bubbles: true, cancelable: true, detail: { autoplayMedia: false } }));
        mediaTarget.querySelector('.shopify-model-viewer-ui__button')?.setAttribute('tabindex', 0);
        mediaTarget.querySelector('.product-single__close-media')?.setAttribute('tabindex', 0);
      }
    }
  }

  scrollToStackedMedia(index) {
    const mediaTarget = this.container.querySelectorAll('.product-slideshow .product-main-slide')[index];
    if (!mediaTarget) return;
    window.scroll({ top: mediaTarget.offsetTop, behavior: 'smooth' });
  }

  stackedMediaInit() {
    const mediaGalleryElements = this.container.querySelectorAll('.product-slideshow .product-main-slide');
    this.mediaObservers = [];
    mediaGalleryElements.forEach((slideElement, index) => {
      const mediaObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this.settings.currentSlideIndex = index;
            this.handleStackedMediaChange(index);
          }
        });
      }, { root: null, rootMargin: '400px 0px 0px 0px', threshold: [0, 0.5, 1] });
      mediaObserver.observe(slideElement);
      this.mediaObservers.push(mediaObserver);
    });
  }

  initProductSlider(variant) {
    if (this.cache.mainSlider.querySelectorAll(this.selectors.slide).length <= 1) {
      const slide = this.cache.mainSlider.querySelector(this.selectors.slide)
      if (slide) slide.classList.add('is-selected')
      return
    }

    if (this.flickity?.destroy) this.flickity.destroy()

    if (!variant) {
      const activeSlide = this.cache.mainSlider.querySelector(this.selectors.startingSlide)
      this.settings.currentSlideIndex = this._slideIndex(activeSlide)
    }

    let mainSliderArgs = {
      dragThreshold: theme.config.isTouch ? 80 : 25,
      adaptiveHeight: false,
      avoidReflow: true,
      initialIndex: this.settings.currentSlideIndex,
      childNav: this.cache.thumbSlider,
      childNavScroller: this.cache.thumbScroller,
      childVertical: this.cache.thumbSlider?.dataset.position === 'beside',
      pageDots: true,
      wrapAround: true,
      callbacks: {
        onInit: this.onSliderInit.bind(this),
        onChange: this.onSlideChange.bind(this)
      }
    }

    if (this.settings.imageSetName) {
      const imageSetArgs = this.imageSetArguments(variant)
      mainSliderArgs = Object.assign({}, mainSliderArgs, imageSetArgs)
      this.updateImageSetThumbs(mainSliderArgs.imageSet)
    }

    if (!theme.config.bpSmall && this.settings.mediaGalleryLayout === 'stacked') {
      this.container.querySelector(this.selectors.imageContainer)?.setAttribute('data-has-slideshow', 'false');
      this.stackedMediaInit();
      return;
    }

    this.flickity = new Slideshow(this.cache.mainSlider, mainSliderArgs)
    setTimeout(() => this.flickity?.resize(), 100)
  }

  onSliderInit(slide) {
    if (this.settings.imageSetName && slide) this.prepMediaOnSlide(slide)
  }

  onSlideChange(index) {
    if (!this.flickity) return
    const prevSlide = this.cache.mainSlider.querySelector('.product-main-slide[data-index="' + this.settings.currentSlideIndex + '"]')
    const nextSlide = this.settings.imageSetName
      ? this.cache.mainSlider.querySelectorAll('.flickity-slider .product-main-slide')[index]
      : this.cache.mainSlider.querySelector('.product-main-slide[data-index="' + index + '"]')

    if (prevSlide) {
      prevSlide.setAttribute('tabindex', '-1')
      this.stopMediaOnSlide(prevSlide)
    }
    if (nextSlide) {
      nextSlide.setAttribute('tabindex', 0)
      this.prepMediaOnSlide(nextSlide)
    }
    this.settings.currentSlideIndex = index
    setTimeout(() => this.flickity?.resize(), 100);
  }

  stopMediaOnSlide(slide) {
    if (!slide) return
    const video = slide.querySelector(this.selectors.productVideo)
    if (video) {
      const videoType = this._getVideoType(video)
      const videoId = this._getVideoDivId(video)
      if (videoType === 'youtube' && this.videoObjects[videoId]?.videoPlayer) {
        this.videoObjects[videoId].videoPlayer.stopVideo()
        return
      } else if (videoType === 'mp4') {
        this.stopMp4Video(videoId)
        return
      }
    }
    slide.querySelector(this.selectors.media)?.dispatchEvent(new CustomEvent('mediaHidden', { bubbles: true, cancelable: true }))
  }

  prepMediaOnSlide(slide) {
    if (!slide) return
    const video = slide.querySelector(this.selectors.productVideo)
    if (video) {
      this.flickity?.reposition()
      const videoType = this._getVideoType(video)
      const videoId = this._getVideoDivId(video)
      if (videoType === 'youtube' && this.videoObjects[videoId]?.videoPlayer && this.videoObjects[videoId].options.style !== 'sound') {
        this.videoObjects[videoId].videoPlayer.playVideo()
        return
      } else if (videoType === 'mp4') {
        this.playMp4Video(videoId)
      }
    }
    const nextMedia = slide.querySelector(this.selectors.media)
    if (nextMedia) {
      nextMedia.dispatchEvent(new CustomEvent('mediaVisible', { bubbles: true, cancelable: true, detail: { autoplayMedia: true } }))
      slide.querySelector('.shopify-model-viewer-ui__button')?.setAttribute('tabindex', 0)
      slide.querySelector('.product-single__close-media')?.setAttribute('tabindex', 0)
    }
    setTimeout(() => this.flickity?.resize(), 200);
  }

  _slideIndex(el) { return el?.getAttribute('data-index') }

  updateModalProductInventory() {
    window.inventories = window.inventories || {}
    this.container.querySelectorAll('.js-product-inventory-data').forEach(el => {
      const productId = el.dataset.productId
      window.inventories[productId] = {}
      el.querySelectorAll('.js-variant-inventory-data').forEach(v => {
        window.inventories[productId][v.dataset.id] = {
          quantity: v.dataset.quantity,
          policy: v.dataset.policy,
          incoming: v.dataset.incoming,
          next_incoming_date: v.dataset.date
        }
      })
    })
  }

  initModelViewerLibraries() {
    const modelViewerElements = this.container.querySelectorAll(this.selectors.media)
    if (modelViewerElements.length < 1) return
    theme.ProductMedia.init(modelViewerElements, this.sectionId)
  }

  initShopifyXrLaunch() {
    document.addEventListener('shopify_xr_launch', () => {
      const currentMedia = this.container.querySelector(`${this.selectors.productMediaWrapper}:not(.${this.classes.hidden})`)
      currentMedia?.dispatchEvent(new CustomEvent('xrLaunch', { bubbles: true, cancelable: true }))
    })
  }

  customMediaListeners() {
    document.querySelectorAll(this.selectors.closeMedia).forEach(el => {
      el.addEventListener('click', () => {
        const slide = this.settings.mediaGalleryLayout === 'stacked'
          ? this.cache.mainSlider.querySelector(`.product-main-slide[data-index="${this.settings.currentSlideIndex}"]`)
          : this.cache.mainSlider.querySelector(this.selectors.currentSlide);
        slide?.querySelector(this.selectors.media)?.dispatchEvent(new CustomEvent('mediaHidden', { bubbles: true, cancelable: true }))
      })
    })

    this.container.querySelectorAll('model-viewer').forEach(el => {
      el.addEventListener('shopify_model_viewer_ui_toggle_play', this.mediaLoaded.bind(this))
      el.addEventListener('shopify_model_viewer_ui_toggle_pause', this.mediaUnloaded.bind(this))
    })
  }

  mediaLoaded(evt) {
    this.container.querySelectorAll(this.selectors.closeMedia).forEach(el => el.classList.remove(this.classes.hidden))
    this.flickity?.setDraggable(false)
  }

  mediaUnloaded(evt) {
    this.container.querySelectorAll(this.selectors.closeMedia).forEach(el => el.classList.add(this.classes.hidden))
    this.flickity?.setDraggable(true)
  }

  onUnload() {
    theme.ProductMedia.removeSectionModels(this.sectionId);
    this.flickity?.destroy?.();
    this.photoswipe?.destroy?.();
    this.mediaObservers?.forEach(obs => obs.disconnect());
  }
}

customElements.define('product-component', Product)
